import React, {lazy} from 'react';
import {Navigate, Route, Routes as Switch, useLocation} from 'react-router-dom';
import {
  ClientPortalModulePermissionCodes,
  ConfigureModulePermissionCodes,
  DataManagementModulePermissionCodes,
  DigitalShelfModulePermissionCodes,
  HomeModulePermissionCodes,
  InternalToolsModulePermissionCodes,
  InventoryModulePermissionCodes,
  LaunchModulePermissionCodes,
  MeasureModulePermissionCodes,
} from 'utils/types';
import {measureTabs} from 'pages/dashboard/measureTabs';
import {dataLoadingTabs} from 'pages/data-loading/dataLoadingTabs';
import {internalTabs} from 'pages/internal/internalTabs';
import {analysisTabs} from 'pages/launch/tabs/analysis/analysisTabs';
import {launchTabs} from 'pages/launch/launchTabs';
import {batchAnalysisTabs} from 'pages/batch-analysis/batchAnalysisTabs';
import {campaignPlanningTabs} from 'pages/campaign-planning/campaignPlanningTabs';
import {clientProfileTabs} from 'pages/client-profile/clientProfileTabs';
import {PathxGuard, PathxTabGuard} from './pathxRouteGuard';

function NavigatePreserveSearch({to}: {to: string}) {
  const {search} = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

const SignIn = lazy(() => import('pages/auth/sign-in'));
const SignUp = lazy(() => import('pages/auth/sign-up'));
const Profile = lazy(() => import('pages/profile'));
const PasswordReset = lazy(() => import('pages/auth/password-reset'));
const ChangePassword = lazy(() => import('pages/auth/ChangePassword'));
const Home = lazy(() => import('pages/home/Home2'));
const MultiCampaignDashboard = lazy(() => import('pages/multi-campaign/index'));
const ReportDelivery = lazy(() => import('pages/report-delivery'));
const ConfirmEmail = lazy(() => import('pages/confirm-email'));
const Payment = lazy(() => import('pages/payment'));
const Dashboard = lazy(() => import('pages/dashboard'));
const ReportHistory = lazy(() => import('pages/report-history'));
const DigitalShelf = lazy(() => import('pages/digital-shelf2/DigitalShelf2'));
const PaymentInvoice = lazy(() => import('pages/payment/Invoice'));
const Inventory = lazy(() => import('pages/inventory2/Inventory'));
const InventoryTest = lazy(() => import('pages/inventory3/Inventory'));
const DataLoading = lazy(() => import('pages/data-loading/DataLoading'));
const Launch = lazy(() => import('pages/launch'));
const Internal = lazy(() => import('pages/internal'));
const MondayTracker = lazy(() => import('pages/monday-tracker'));
const InventoryItemList = lazy(() => import('pages/inventory-item-list/InventoryItemList'));
const PublicCampaignForm = lazy(() => import('pages/public/PublicCampaignForm'));
const Clients = lazy(() => import('pages/client/Client'));
const ClientProfile = lazy(() => import('pages/client-profile/ClientProfile'));
const DigitalShelfHealth = lazy(() => import('pages/ds-health/DigitalShelfHealth'));
const StoreDataLoading = lazy(() => import('pages/store-data-loading/StoreDataLoading'));
const AreaDataLoading = lazy(() => import('pages/area-data-loading/AreaDataLoading'));
const EditScheduledTaskModal = lazy(() => import('pages/digital-shelf2/scheduled-task/EditScheduledTaskModal'));
const ResultsLogModal = lazy(() => import('pages/digital-shelf2/results-log/ResultsLog'));
const CampaignPlanning = lazy(() => import('pages/campaign-planning'));
const LandingPage = lazy(() => import('pages/landing-page'));
const SocialListening = lazy(() => import('pages/social-listening/SocialListening'));
const UserManagement = lazy(() => import('pages/users/UserManagement'));
const CampaignBenchmarkDataPage = lazy(() => import('pages/campaign-benchmark-data/CampaignBenchmarkDataPage'));
const Brands = lazy(() => import('pages/brand/Brand'));
const Categories = lazy(() => import('pages/category/Category'));
const HubspotDifferences = lazy(() => import('pages/hubspot-differences/HubspotDifferences'));
const PptxExportPage = lazy(() => import('pages/pptx-export/PptxExportPage'));

export const Routes = (): React.ReactElement => (
  <Switch>
    <Route path="/sign-in" element={<SignIn />} />
    <Route path="/sign-up" element={<SignUp />} />
    <Route path="/password-reset" element={<PasswordReset />} />
    <Route path="/change-password" element={<ChangePassword />} />

    <Route
      path="/landing-page/:landingPageName"
      element={<PathxGuard requiredAnyPermission={[HomeModulePermissionCodes.view_home]} element={<LandingPage />} />}
    />
    <Route
      path="/home"
      element={<PathxGuard requiredAnyPermission={[HomeModulePermissionCodes.view_home]} element={<Home />} />}
    />
    <Route
      path="/multi-campaign"
      element={
        <PathxGuard
          requiredAnyPermission={[HomeModulePermissionCodes.view_multi_campaign_dashboard]}
          element={<MultiCampaignDashboard />}
        />
      }
    />
    <Route path="/profile" element={<Profile />} />
    <Route
      path="/inventory"
      element={
        <PathxGuard requiredAnyPermission={[InventoryModulePermissionCodes.view_inventory]} element={<Inventory />} />
      }
    />

    <Route
      path="/data-loading"
      element={
        <PathxGuard
          requiredAnyPermission={[DataManagementModulePermissionCodes.view_data_management]}
          element={<DataLoading />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/data-loading/:tabName"
      element={
        <PathxTabGuard
          tabDef={dataLoadingTabs}
          tabParamKey="tabName"
          element={<DataLoading />}
          campaignIdParamKey="campaign"
        />
      }
    />

    <Route path="/report-delivery" element={<ReportDelivery />} />
    <Route path="/confirm-email" element={<ConfirmEmail />} />
    <Route path="/payment" element={<Payment />} />
    <Route path="/payment/invoice/:id" element={<PaymentInvoice />} />
    <Route path="/dashboard2" element={<NavigatePreserveSearch to="/dashboard/sales_report" />} />
    <Route path="/dashboard/qs_sales_report" element={<NavigatePreserveSearch to="/dashboard/sales_report" />} />
    <Route
      path="/dashboard/qs_pre_sales_report"
      element={<NavigatePreserveSearch to="/dashboard/pre_sales_report" />}
    />
    <Route path="/dashboard/sales_lift_exp" element={<NavigatePreserveSearch to="/dashboard/sales_lift" />} />

    <Route
      path="/dashboard"
      element={
        <PathxGuard
          requiredAnyPermission={[MeasureModulePermissionCodes.view_measure]}
          element={<Dashboard />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/dashboard/:reportView"
      element={
        <PathxTabGuard
          tabDef={measureTabs}
          tabParamKey="reportView"
          element={<Dashboard />}
          campaignIdParamKey="campaign"
        />
      }
    />

    <Route
      path="/report-history"
      element={
        <PathxGuard
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_internal_tools]}
          element={<ReportHistory />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/digital-shelf"
      element={
        <PathxGuard
          requiredAnyPermission={[DigitalShelfModulePermissionCodes.view_digital_shelf]}
          element={<DigitalShelf />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/digital-shelf/:tabName"
      element={
        <PathxGuard
          requiredAnyPermission={[DigitalShelfModulePermissionCodes.view_digital_shelf]}
          element={<DigitalShelf />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route path="/digital-shelf/snapshot" element={<Navigate to="/digital-shelf/research" replace />} />
    <Route path="/digital-shelf/overtime" element={<Navigate to="/digital-shelf/research" replace />} />
    <Route
      path="/digital-shelf/setup/:id/edit"
      element={
        <PathxGuard
          requiredAnyPermission={[DigitalShelfModulePermissionCodes.view_digital_shelf]}
          element={<EditScheduledTaskModal />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/digital-shelf/setup/:id/results-log/:date"
      element={
        <PathxGuard
          requiredAnyPermission={[DigitalShelfModulePermissionCodes.view_digital_shelf]}
          element={<ResultsLogModal />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/launch"
      element={
        <PathxGuard
          requiredAnyPermission={[LaunchModulePermissionCodes.view_launch]}
          element={<Launch />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/launch/:tabName/:subName?/:openModal?"
      element={
        <PathxTabGuard tabDef={launchTabs} tabParamKey="tabName" element={<Launch />} campaignIdParamKey="campaign" />
      }
    />

    <Route
      path="/campaign-planning"
      element={
        <PathxGuard
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_internal_tools]}
          element={<Launch />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/campaign-planning/:tabName"
      element={
        <PathxTabGuard
          tabDef={campaignPlanningTabs}
          tabParamKey="tabName"
          element={<CampaignPlanning />}
          campaignIdParamKey="campaign"
        />
      }
    />

    <Route
      path="/configure"
      element={
        <PathxGuard
          requiredAnyPermission={[ConfigureModulePermissionCodes.view_configure]}
          element={<Launch />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/configure/:tabName?/:openModal?"
      element={
        <PathxTabGuard tabDef={analysisTabs} tabParamKey="tabName" element={<Launch />} campaignIdParamKey="campaign" />
      }
    />

    <Route
      path="/internal"
      element={
        <PathxGuard
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_internal_tools]}
          element={<Internal />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/internal/:tabName"
      element={
        <PathxTabGuard
          tabDef={internalTabs}
          tabParamKey="tabName"
          element={<Internal />}
          campaignIdParamKey="campaign"
        />
      }
    />
    <Route
      path="/internal/:tabName/:subName"
      element={
        <PathxTabGuard
          tabDef={batchAnalysisTabs}
          tabParamKey="subName"
          element={<Internal />}
          campaignIdParamKey="campaign"
        />
      }
    />

    <Route
      path="/project-tracker"
      element={
        <PathxGuard
          requiredAnyPermission={[HomeModulePermissionCodes.view_monday_project_tracker]}
          element={<MondayTracker />}
        />
      }
    />
    <Route path="/inventory-item-list" element={<InventoryItemList />} />
    <Route
      path="/client"
      element={<PathxGuard requireStaff requireSuperuser requiredAnyPermission={[]} element={<Clients />} />}
    />
    {/* campaignScoped={false}: the client profile is about the client's account, not a campaign,
        so it is checked against the user's own permissions rather than whichever campaign happens
        to be in context. */}
    <Route
      path="/client-profile"
      element={
        <PathxGuard
          requiredAnyPermission={[ClientPortalModulePermissionCodes.view_client_profile]}
          campaignScoped={false}
          element={<ClientProfile />}
        />
      }
    />
    <Route
      path="/client-profile/:tabName"
      element={
        <PathxTabGuard
          tabDef={clientProfileTabs}
          tabParamKey="tabName"
          campaignScoped={false}
          element={<ClientProfile />}
        />
      }
    />
    <Route
      path="/brand"
      element={
        <PathxGuard
          requireStaff
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_brands]}
          element={<Brands />}
        />
      }
    />
    <Route
      path="/category"
      element={
        <PathxGuard
          requireStaff
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_category_page]}
          element={<Categories />}
        />
      }
    />
    <Route path="/inventory-test" element={<InventoryTest />} />
    <Route path="/ds-health" element={<DigitalShelfHealth />} />
    <Route path="/store-data-loading" element={<StoreDataLoading />} />
    <Route path="/area-data-loading" element={<AreaDataLoading />} />

    <Route path="/public" element={<PublicCampaignForm />} />
    <Route
      path="/social-listening"
      element={<PathxGuard requiredAnyPermission={[]} requireStaff element={<SocialListening />} />}
    />
    <Route
      path="/users"
      element={<PathxGuard requireStaff requiredAnyPermission={[]} element={<UserManagement />} />}
    />

    <Route
      path="/campaign-benchmark-data"
      element={
        <PathxGuard
          requireStaff
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_campaign_benchmark_data]}
          element={<CampaignBenchmarkDataPage />}
        />
      }
    />
    <Route
      path="/hubspot-differences"
      element={
        <PathxGuard
          requireStaff
          requiredAnyPermission={[InternalToolsModulePermissionCodes.view_hubspot_differences]}
          element={<HubspotDifferences />}
        />
      }
    />
    <Route
      path="/pptx-export"
      element={
        <PathxGuard requiredAnyPermission={[LaunchModulePermissionCodes.view_launch]} element={<PptxExportPage />} />
      }
    />
  </Switch>
);
